import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/SubaccountsAnticipations_CancelInput.ts';
import '../contracts/SubaccountsAnticipations_ImportInput.ts';
import '../contracts/SubaccountsAnticipations_LinkRecebimentoInput.ts';
import '../contracts/SubaccountsAnticipations_RequestInput.ts';
import '../contracts/SubaccountsAnticipations_SimulateInput.ts';
import '../contracts/SubaccountsAnticipations_SyncInput.ts';
import '../contracts/SubaccountsAnticipations_UpdateConfigInput.ts';

import '../contracts/SubaccountsAnticipations_LimitsResponse';
import '../contracts/SubaccountsAnticipations_CancelResponse';
import '../contracts/SubaccountsAnticipations_ConfigResponse';
import '../contracts/SubaccountsAnticipations_DetalheResponse';
import '../contracts/SubaccountsAnticipations_ImportResponse';
import '../contracts/SubaccountsAnticipations_LinkRecebimentoResponse';
import '../contracts/SubaccountsAnticipations_RequestResponse';
import '../contracts/SubaccountsAnticipations_SimulateResponse';
import '../contracts/SubaccountsAnticipations_StatsResponse';
import '../contracts/SubaccountsAnticipations_StatusesResponse';
import '../contracts/SubaccountsAnticipations_SyncResponse';
import '../contracts/SubaccountsAnticipations_SyncSubaccountInput';
import '../contracts/SubaccountsAnticipations_SyncSubaccountResponse';
import '../contracts/SubaccountsAnticipations_UnlinkRecebimentoResponse';
import '../contracts/SubaccountsAnticipations_UpdateConfigResponse';
import type { SubaccountsAnticipations_LinkRecebimentoInput } from '../contracts/SubaccountsAnticipations_LinkRecebimentoInput';
import type { SubaccountsAnticipations_RequestInput } from '../contracts/SubaccountsAnticipations_RequestInput';
import type { SubaccountsAnticipations_SimulateInput } from '../contracts/SubaccountsAnticipations_SimulateInput';
import type { SubaccountsAnticipations_UpdateConfigInput } from '../contracts/SubaccountsAnticipations_UpdateConfigInput';
// Status constants - exported for external use
export const ANTICIPATION_STATUS = {
  PENDING: 'PENDING',
  SCHEDULED: 'SCHEDULED',
  CREDITED: 'CREDITED',
  DEBITED: 'DEBITED',
  DENIED: 'DENIED',
  CANCELLED: 'CANCELLED',
  OVERDUE: 'OVERDUE',
};

// Final statuses (no further updates expected)
export const FINAL_STATUSES = [
  ANTICIPATION_STATUS.DEBITED,
  ANTICIPATION_STATUS.DENIED,
  ANTICIPATION_STATUS.CANCELLED,
];

/**
 * Composable for managing Anticipations (Antecipacao de Recebiveis)
 *
 * ARCHITECTURE:
 * =============
 * Anticipation allows receiving future payments earlier with a fee.
 * Since ASAAS doesn't send webhooks for anticipation events,
 * status sync is done via polling (twice daily cron job).
 *
 * Features:
 * - Get/update anticipation config per subaccount
 * - Query anticipation limits
 * - Simulate and request anticipations
 * - Track anticipation status and history
 * - Link anticipations to recebimentos (M:N)
 *
 * @see claudedocs/anticipation-feature-implementation-plan.md
 */
export function useAnticipations() {
  const isLoading = ref(false);
  const error = ref(null);

  // ===========================================================================
  // CONFIGURATION
  // ===========================================================================

  /**
   * Get anticipation configuration for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Configuration with local_config, provider_config, supports flags
   */
  const getConfig = async (subaccountId: number) => {
    log('useAnticipations', 'getConfig', 'Fetching config', { subaccountId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/anticipations/config/${subaccountId}`, { responseContract: 'SubaccountsAnticipations_ConfigResponse' });
      log('useAnticipations', 'getConfig', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar configuracao de antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'getConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update anticipation configuration for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} config - Configuration data
   * @param {boolean} config.enabled - Enable/disable anticipation
   * @param {boolean} config.auto_credit_card - Auto-anticipate credit card charges
   * @param {number} config.min_amount - Minimum amount in cents
   * @returns {Object} Updated configuration
   */
  const updateConfig = async (subaccountId: any, config: SubaccountsAnticipations_UpdateConfigInput) => {
    log('useAnticipations', 'updateConfig', 'Updating config', { subaccountId, config });
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsAnticipations_UpdateConfigInput', config);
      const response = await httpService.put(
        `/index.php/subaccounts/anticipations/update_config/${subaccountId}`,
        config,
        { responseContract: 'SubaccountsAnticipations_UpdateConfigResponse' }
      );
      log('useAnticipations', 'updateConfig', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar configuracao');
      }
    } catch (err) {
      log('useAnticipations', 'updateConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // LIMITS
  // ===========================================================================

  /**
   * Get anticipation limits for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Limits data from provider
   */
  const getLimits = async (subaccountId: number) => {
    log('useAnticipations', 'getLimits', 'Fetching limits', { subaccountId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/anticipations/limits/${subaccountId}`, { responseContract: 'SubaccountsAnticipations_LimitsResponse' });
      log('useAnticipations', 'getLimits', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar limites de antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'getLimits', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // LIST & DETAILS
  // ===========================================================================

  /**
   * List anticipations for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} filters - Optional filters
   * @param {string} filters.status - Filter by status
   * @param {number} filters.limit - Max records (default 50)
   * @param {number} filters.offset - Pagination offset
   * @returns {Array} Array of anticipations
   */
  const listBySubaccount = async (subaccountId: any, filters: any = {}) => {
    log('useAnticipations', 'listBySubaccount', 'Fetching anticipations', { subaccountId, filters });
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if ((filters as any).status) params.append('status', (filters as any).status);
      if ((filters as any).limit) params.append('limit', (filters as any).limit);
      if ((filters as any).offset) params.append('offset', (filters as any).offset);

      const queryString = params.toString();
      const url = `/index.php/subaccounts/anticipations/lista/${subaccountId}${queryString ? '?' + queryString : ''}`;

      const response = await httpService.get<ApiResponse<unknown[]>>(url);
      log('useAnticipations', 'listBySubaccount', 'Response', response);

      if (response.success && Array.isArray(response.dados)) {
        return response.dados || [];
      } else {
        return [];
      }
    } catch (err) {
      log('useAnticipations', 'listBySubaccount', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get anticipation by ID with full details
   *
   * @param {number} anticipationId - Anticipation ID
   * @returns {Object} Anticipation with recebimentos
   */
  const getById = async (anticipationId: number) => {
    log('useAnticipations', 'getById', 'Fetching anticipation', anticipationId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/anticipations/detalhe/${anticipationId}`, { responseContract: 'SubaccountsAnticipations_DetalheResponse' });
      log('useAnticipations', 'getById', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Antecipacao nao encontrada');
      }
    } catch (err) {
      log('useAnticipations', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get all status info for frontend display
   *
   * @returns {Object} Status info keyed by status code
   */
  const getStatuses = async () => {
    log('useAnticipations', 'getStatuses', 'Fetching statuses');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/subaccounts/anticipations/statuses', { responseContract: 'SubaccountsAnticipations_StatusesResponse' });
      log('useAnticipations', 'getStatuses', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar status');
      }
    } catch (err) {
      log('useAnticipations', 'getStatuses', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // OPERATIONS
  // ===========================================================================

  /**
   * Simulate anticipation (preview fees and net value)
   *
   * @param {Object} data - Simulation data
   * @param {number} data.subaccount_id - Subaccount ID
   * @param {string} data.payment_id - Provider payment ID
   * @param {string} data.installment_id - Optional: specific installment
   * @returns {Object} Simulation result with fees
   */
  const simulate = async (data: SubaccountsAnticipations_SimulateInput) => {
    log('useAnticipations', 'simulate', 'Simulating anticipation', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsAnticipations_SimulateInput', data);
      const response = await httpService.post('/index.php/subaccounts/anticipations/simulate', data, { responseContract: 'SubaccountsAnticipations_SimulateResponse' });
      log('useAnticipations', 'simulate', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao simular antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'simulate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Request anticipation
   *
   * @param {Object} data - Request data
   * @param {number} data.subaccount_id - Subaccount ID
   * @param {string} data.payment_id - Provider payment ID
   * @param {string} data.installment_id - Optional: specific installment
   * @param {number} data.payment_link_id - Optional: link to payment_link
   * @returns {Object} Created anticipation
   */
  const request = async (data: SubaccountsAnticipations_RequestInput) => {
    log('useAnticipations', 'request', 'Requesting anticipation', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsAnticipations_RequestInput', data);
      const response = await httpService.post('/index.php/subaccounts/anticipations/request', data, { responseContract: 'SubaccountsAnticipations_RequestResponse' });
      log('useAnticipations', 'request', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao solicitar antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'request', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel anticipation request
   *
   * @param {number} anticipationId - Anticipation ID
   * @returns {Object} Cancellation result
   */
  const cancel = async (anticipationId: number) => {
    log('useAnticipations', 'cancel', 'Cancelling anticipation', anticipationId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/subaccounts/anticipations/cancel/${anticipationId}`, null, { inputContract: 'SubaccountsAnticipations_CancelInput', responseContract: 'SubaccountsAnticipations_CancelResponse' });
      log('useAnticipations', 'cancel', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao cancelar antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'cancel', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // SYNC
  // ===========================================================================

  /**
   * Sync a single anticipation from provider
   *
   * @param {number} anticipationId - Anticipation ID
   * @returns {Object} Sync result with updated anticipation
   */
  const syncAnticipation = async (anticipationId: number) => {
    log('useAnticipations', 'syncAnticipation', 'Syncing anticipation', anticipationId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/subaccounts/anticipations/sync/${anticipationId}`, null, { inputContract: 'SubaccountsAnticipations_SyncInput', responseContract: 'SubaccountsAnticipations_SyncResponse' });
      log('useAnticipations', 'syncAnticipation', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao sincronizar antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'syncAnticipation', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sync all anticipations for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Sync results summary
   */
  const syncSubaccount = async (subaccountId: number) => {
    log('useAnticipations', 'syncSubaccount', 'Syncing subaccount', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/subaccounts/anticipations/sync_subaccount/${subaccountId}`, null, { inputContract: 'SubaccountsAnticipations_SyncSubaccountInput', responseContract: 'SubaccountsAnticipations_SyncSubaccountResponse' });
      log('useAnticipations', 'syncSubaccount', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao sincronizar antecipacoes');
      }
    } catch (err) {
      log('useAnticipations', 'syncSubaccount', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Import anticipations from provider
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Import results
   */
  const importFromProvider = async (subaccountId: number) => {
    log('useAnticipations', 'importFromProvider', 'Importing from provider', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/subaccounts/anticipations/import/${subaccountId}`, null, { inputContract: 'SubaccountsAnticipations_ImportInput', responseContract: 'SubaccountsAnticipations_ImportResponse' });
      log('useAnticipations', 'importFromProvider', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao importar antecipacoes');
      }
    } catch (err) {
      log('useAnticipations', 'importFromProvider', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // RECEBIMENTO LINKS
  // ===========================================================================

  /**
   * Link anticipation to recebimento
   *
   * @param {number} recebimentoId - Recebimento ID
   * @param {number} anticipationId - Anticipation ID
   * @returns {Object} Link result
   */
  const linkRecebimento = async (recebimentoId: any, anticipationId: any) => {
    log('useAnticipations', 'linkRecebimento', 'Linking', { recebimentoId, anticipationId });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: SubaccountsAnticipations_LinkRecebimentoInput = {
        recebimento_id: recebimentoId,
        anticipation_id: anticipationId,
      };
      validate('SubaccountsAnticipations_LinkRecebimentoInput', payload);
      const response = await httpService.post('/index.php/subaccounts/anticipations/link_recebimento', payload, { responseContract: 'SubaccountsAnticipations_LinkRecebimentoResponse' });
      log('useAnticipations', 'linkRecebimento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao vincular antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'linkRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Unlink anticipation from recebimento
   *
   * @param {number} recebimentoId - Recebimento ID
   * @param {number} anticipationId - Anticipation ID
   * @returns {Object} Unlink result
   */
  const unlinkRecebimento = async (recebimentoId: any, anticipationId: any) => {
    log('useAnticipations', 'unlinkRecebimento', 'Unlinking', { recebimentoId, anticipationId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete('/index.php/subaccounts/anticipations/unlink_recebimento', {
        recebimento_id: recebimentoId,
        anticipation_id: anticipationId,
        responseContract: 'SubaccountsAnticipations_UnlinkRecebimentoResponse',
        inputContract: 'SubaccountsAnticipations_LinkRecebimentoInput',
      } as any);
      log('useAnticipations', 'unlinkRecebimento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao desvincular antecipacao');
      }
    } catch (err) {
      log('useAnticipations', 'unlinkRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get anticipations for a recebimento
   *
   * @param {number} recebimentoId - Recebimento ID
   * @returns {Array} Array of anticipations
   */
  const getByRecebimento = async (recebimentoId: number) => {
    log('useAnticipations', 'getByRecebimento', 'Fetching anticipations', recebimentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/anticipations/by_recebimento/${recebimentoId}`, { responseContract: 'SubaccountsAnticipations_ByRecebimentoResponse' });
      log('useAnticipations', 'getByRecebimento', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        return [];
      }
    } catch (err) {
      log('useAnticipations', 'getByRecebimento', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // STATISTICS
  // ===========================================================================

  /**
   * Get anticipation statistics for a subaccount
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Statistics with counts and totals
   */
  const getStats = async (subaccountId: number) => {
    log('useAnticipations', 'getStats', 'Fetching stats', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/anticipations/stats/${subaccountId}`, { responseContract: 'SubaccountsAnticipations_StatsResponse' });
      log('useAnticipations', 'getStats', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar estatisticas');
      }
    } catch (err) {
      log('useAnticipations', 'getStats', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // UTILITY FUNCTIONS
  // ===========================================================================

  /**
   * Get status display configuration
   *
   * @param {string} status - Status code
   * @returns {Object} Status config with label, severity, icon
   */
  const getStatusConfig = (status: any) => {
    const configs = {
      'PENDING': { label: 'Pendente', severity: 'warn', icon: 'pi-clock' },
      'SCHEDULED': { label: 'Agendado', severity: 'info', icon: 'pi-calendar' },
      'CREDITED': { label: 'Creditado', severity: 'success', icon: 'pi-check-circle' },
      'DEBITED': { label: 'Liquidado', severity: 'info', icon: 'pi-check' },
      'DENIED': { label: 'Negado', severity: 'danger', icon: 'pi-times-circle' },
      'CANCELLED': { label: 'Cancelado', severity: 'secondary', icon: 'pi-ban' },
      'OVERDUE': { label: 'Vencido', severity: 'danger', icon: 'pi-exclamation-triangle' },
    };
    return (configs as any)[status] || { label: status, severity: 'secondary', icon: 'pi-question' };
  };

  /**
   * Check if status is final (no more updates expected)
   *
   * @param {string} status - Status code
   * @returns {boolean} True if final
   */
  const isFinalStatus = (status: any) => {
    return FINAL_STATUSES.includes(status);
  };

  /**
   * Check if anticipation can be cancelled
   *
   * @param {Object} anticipation - Anticipation object
   * @returns {boolean} True if can be cancelled
   */
  const canCancel = (anticipation: any) => {
    if (!anticipation) return false;
    return anticipation.status === 'PENDING' || anticipation.status === 'SCHEDULED';
  };

  /**
   * Format amount from cents to currency string
   *
   * @param {number} amountCents - Amount in cents
   * @returns {string} Formatted amount (e.g., "R$ 150,00")
   */
  const formatAmount = (amountCents: any) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format((amountCents || 0) / 100);
  };

  /**
   * Format fee percentage
   *
   * @param {number} totalValue - Total value in cents
   * @param {number} fee - Fee in cents
   * @returns {string} Formatted percentage (e.g., "2.5%")
   */
  const formatFeePercentage = (totalValue: any, fee: any) => {
    if (!totalValue || !fee) return '0%';
    const percentage = (fee / totalValue) * 100;
    return percentage.toFixed(2) + '%';
  };

  return {
    // State
    isLoading,
    error,

    // Constants
    ANTICIPATION_STATUS,
    FINAL_STATUSES,

    // Configuration
    getConfig,
    updateConfig,

    // Limits
    getLimits,

    // List & Details
    listBySubaccount,
    getById,
    getStatuses,

    // Operations
    simulate,
    request,
    cancel,

    // Sync
    syncAnticipation,
    syncSubaccount,
    importFromProvider,

    // Recebimento Links
    linkRecebimento,
    unlinkRecebimento,
    getByRecebimento,

    // Statistics
    getStats,

    // Utility functions
    getStatusConfig,
    isFinalStatus,
    canCancel,
    formatAmount,
    formatFeePercentage,
  };
}
